/**
 * Cart Upsell — registers the <bw-cart-upsell> custom element and fetches
 * the active offer from the app proxy at /apps/cart-upsell.
 *
 * Supports two display layouts driven by the promo's displayLayout field:
 *   - "block":    stacked rows (image right, text left, "Add to cart" button below)
 *   - "carousel": swipeable carousel of cards
 */
(function () {
  if (customElements.get("bw-cart-upsell")) return;

  const PROXY_BASE = "/apps/cart-upsell";
  const CURRENCY = "₪";

  async function fetchCart() {
    try {
      const r = await fetch("/cart.js", { credentials: "same-origin" });
      if (!r.ok) return null;
      return await r.json();
    } catch {
      return null;
    }
  }

  async function fetchOffer(cart) {
    const productIds = (cart?.items ?? [])
      .map((i) => "gid://shopify/Product/" + i.product_id)
      .join(",");
    const variantIds = (cart?.items ?? [])
      .map((i) => "gid://shopify/ProductVariant/" + i.variant_id)
      .join(",");
    const total = cart ? cart.total_price / 100 : 0;
    const url = new URL(PROXY_BASE, window.location.origin);
    if (productIds) url.searchParams.set("cart_products", productIds);
    if (variantIds) url.searchParams.set("cart_variants", variantIds);
    url.searchParams.set("cart_total", String(total));
    try {
      const r = await fetch(url.toString(), { credentials: "same-origin" });
      if (!r.ok) return null;
      return await r.json();
    } catch {
      return null;
    }
  }

  async function postEvent(itemId, type) {
    try {
      await fetch(PROXY_BASE + "/event", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ itemId, type }),
      });
    } catch {
      /* ignore */
    }
  }

  async function addToCart(item) {
    const numericVariantId = String(item.variantId).replace(
      "gid://shopify/ProductVariant/",
      "",
    );
    try {
      const r = await fetch("/cart/add.js", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [
            {
              id: parseInt(numericVariantId, 10),
              quantity: 1,
              properties: { _bw_cart_upsell_item_id: item.id },
            },
          ],
        }),
      });
      if (!r.ok) throw new Error("add failed");
      await postEvent(item.id, "add_to_cart");
      window.dispatchEvent(new CustomEvent("cart:refresh"));
      document.dispatchEvent(new CustomEvent("cart:refresh"));
      window.dispatchEvent(new CustomEvent("variant:added"));
      return true;
    } catch (err) {
      console.error("[bw-cart-upsell] add to cart failed:", err);
      return false;
    }
  }

  function formatPrice(value) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en", {
        style: "decimal",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    } catch {
      return value.toFixed(2);
    }
  }

  function money(value) {
    return CURRENCY + formatPrice(value);
  }

  function escapeHTML(str) {
    return String(str).replace(/[&<>"']/g, (s) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    })[s]);
  }

  class CartUpsellElement extends HTMLElement {
    constructor() {
      super();
      this._rendered = false;
    }

    connectedCallback() {
      if (this._rendered) return;
      this._rendered = true;
      this._init();
    }

    disconnectedCallback() {
      if (this._autoplayId) {
        clearInterval(this._autoplayId);
        this._autoplayId = null;
      }
    }

    async _init() {
      const cart = await fetchCart();
      const data = await fetchOffer(cart);
      if (!data || !data.promo || !data.promo.items?.length) {
        return;
      }
      this._promo = data.promo;
      this._render();
      for (const item of this._promo.items) {
        postEvent(item.id, "impression");
      }
    }

    _render() {
      const promo = this._promo;
      const layout = promo.displayLayout === "carousel" ? "carousel" : "block";
      const customCss = this.getAttribute("custom-css") || "";
      const addText = this.getAttribute("add-to-cart-button-text") || "Add to cart";
      const soldOutText = this.getAttribute("sold-out-button-text") || "Sold out";
      const showNav =
        (this.getAttribute("show-navigation") ?? "true") !== "false";
      const autoplay =
        (this.getAttribute("autoplay-enabled") ?? "false") === "true";
      const autoplayDelay = parseInt(
        this.getAttribute("autoplay-delay") || "3500",
        10,
      );

      const styleTag = `<style>${BASE_CSS}\n${customCss}</style>`;

      const itemsHTML = promo.items
        .map((item) => this._renderCard(item, addText, soldOutText))
        .join("");

      let body;
      if (layout === "carousel") {
        body = `
          <div class="bw-carousel" data-show-nav="${showNav}">
            ${
              showNav
                ? `<button class="bw-carousel-nav bw-carousel-prev" aria-label="Previous">›</button>`
                : ""
            }
            <div class="bw-carousel-track">
              ${promo.items
                .map(
                  (item) =>
                    `<div class="bw-carousel-card">${this._renderCardInner(item, addText, soldOutText)}</div>`,
                )
                .join("")}
            </div>
            ${
              showNav
                ? `<button class="bw-carousel-nav bw-carousel-next" aria-label="Next">‹</button>`
                : ""
            }
          </div>
        `;
      } else {
        body = `<div class="bw-block-list">${itemsHTML}</div>`;
      }

      this.innerHTML = `
        ${styleTag}
        <div class="bw-cart-upsell">
          ${
            promo.description
              ? `<div class="bw-product-description">${escapeHTML(promo.description)}</div>`
              : ""
          }
          ${body}
        </div>
      `;

      this._wireUp(addText, soldOutText);

      if (layout === "carousel" && autoplay) {
        this._startAutoplay(autoplayDelay);
      }
    }

    _renderCard(item, addText, soldOutText) {
      const original = money(item.productPrice);
      const discounted = money(item.discountedPrice);
      const pct = Math.round(item.discountPercent);
      const img = item.productImage
        ? `<img class="bw-product-image" src="${escapeHTML(item.productImage)}" alt="${escapeHTML(item.productTitle)}" />`
        : `<div class="bw-product-image bw-product-image--placeholder"></div>`;
      return `
        <div class="bw-block-card" data-item-id="${escapeHTML(item.id)}">
          <button
            class="bw-carousel-add-to-cart-btn"
            data-item-id="${escapeHTML(item.id)}"
            data-variant-id="${escapeHTML(item.variantId)}"
          >${escapeHTML(addText)}</button>
          <div class="bw-card-text">
            <div class="bw-product-title">${escapeHTML(item.productTitle)}</div>
            ${item.description ? `<div class="bw-product-description">${escapeHTML(item.description)}</div>` : ""}
            <div class="bw-price-row">
              <span class="bw-price-original">${original}</span>
              <span class="bw-price">${discounted}</span>
            </div>
          </div>
          ${pct > 0 ? `<span class="bw-price-pct">-${pct}%</span>` : ""}
          ${img}
        </div>
      `;
    }

    _renderCardInner(item, addText, soldOutText) {
      const original = money(item.productPrice);
      const discounted = money(item.discountedPrice);
      const pct = Math.round(item.discountPercent);
      const img = item.productImage
        ? `<img class="bw-product-image" src="${escapeHTML(item.productImage)}" alt="${escapeHTML(item.productTitle)}" />`
        : `<div class="bw-product-image bw-product-image--placeholder"></div>`;
      return `
        <div class="bw-card-body">
          <div class="bw-card-text">
            <div class="bw-product-title">${escapeHTML(item.productTitle)}</div>
            ${item.description ? `<div class="bw-product-description">${escapeHTML(item.description)}</div>` : ""}
            <div class="bw-price-row">
              <span class="bw-price-original">${original}</span>
              <span class="bw-price">${discounted}</span>
            </div>
          </div>
          ${pct > 0 ? `<span class="bw-price-pct">-${pct}%</span>` : ""}
          ${img}
        </div>
        <button
          class="bw-carousel-add-to-cart-btn"
          data-item-id="${escapeHTML(item.id)}"
          data-variant-id="${escapeHTML(item.variantId)}"
        >${escapeHTML(addText)}</button>
      `;
    }

    _wireUp(addText, soldOutText) {
      this.querySelectorAll("[data-item-id][data-variant-id]").forEach((btn) => {
        btn.addEventListener("click", async (e) => {
          const target = e.currentTarget;
          const itemId = target.getAttribute("data-item-id");
          const item = this._promo.items.find((it) => it.id === itemId);
          if (!item) return;
          target.disabled = true;
          target.textContent = "…";
          const ok = await addToCart(item);
          if (!ok) {
            target.disabled = false;
            target.textContent = soldOutText || addText;
            return;
          }
          target.textContent = "✓";
          setTimeout(() => {
            this._rendered = false;
            this.innerHTML = "";
            this._init();
          }, 800);
        });
      });

      const track = this.querySelector(".bw-carousel-track");
      if (!track) return;
      const prev = this.querySelector(".bw-carousel-prev");
      const next = this.querySelector(".bw-carousel-next");
      const cardWidth = () => {
        const first = track.querySelector(".bw-carousel-card");
        return first ? first.getBoundingClientRect().width + 12 : 200;
      };
      if (prev)
        prev.addEventListener("click", () =>
          track.scrollBy({ left: -cardWidth(), behavior: "smooth" }),
        );
      if (next)
        next.addEventListener("click", () =>
          track.scrollBy({ left: cardWidth(), behavior: "smooth" }),
        );

      const updateCurrent = () => {
        const cards = track.querySelectorAll(".bw-carousel-card");
        if (cards.length === 0) return;
        const trackRect = track.getBoundingClientRect();
        const trackCenter = trackRect.left + trackRect.width / 2;
        let closest = cards[0];
        let closestDist = Infinity;
        cards.forEach((card) => {
          const r = card.getBoundingClientRect();
          const d = Math.abs(r.left + r.width / 2 - trackCenter);
          if (d < closestDist) {
            closestDist = d;
            closest = card;
          }
        });
        cards.forEach((card) =>
          card.classList.toggle(
            "bw-carousel-card--current",
            card === closest,
          ),
        );
      };
      track.addEventListener("scroll", updateCurrent, { passive: true });
      // Initial mark — wait one frame so layout settles
      requestAnimationFrame(updateCurrent);
    }

    _startAutoplay(delay) {
      const track = this.querySelector(".bw-carousel-track");
      if (!track) return;
      if (this._autoplayId) {
        clearInterval(this._autoplayId);
        this._autoplayId = null;
      }
      const isRTL =
        getComputedStyle(track).direction === "rtl" ||
        document.documentElement.dir === "rtl";
      const dir = isRTL ? -1 : 1;
      const step = () => {
        const cardW = (track.querySelector(".bw-carousel-card")?.getBoundingClientRect()
          .width ?? 0) + 12;
        const max = track.scrollWidth - track.clientWidth;
        const atEnd = isRTL
          ? track.scrollLeft - cardW < -max
          : track.scrollLeft + cardW > max;
        if (atEnd) {
          track.scrollTo({ left: 0, behavior: "smooth" });
        } else {
          track.scrollBy({ left: dir * cardW, behavior: "smooth" });
        }
      };
      this._autoplayId = setInterval(step, delay);
      const stop = () => {
        if (this._autoplayId) {
          clearInterval(this._autoplayId);
          this._autoplayId = null;
        }
      };
      track.addEventListener("mouseenter", stop);
      track.addEventListener("touchstart", stop, { passive: true });
      track.addEventListener("pointerdown", stop, { passive: true });
    }
  }

  const BASE_CSS = `
    .bw-cart-upsell { display: block; margin: 1rem 0; }
    .bw-cart-upsell { font-size: 14px; margin-bottom: 0.75rem; }
    .bw-product-description { font-size: 14px; }
    .bw-block-list {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      border-radius: 0.5rem;
      padding: 0.5rem;
    }
    .bw-block-card {
      border-radius: 0.5rem;
      padding: 0.75rem;
      background: #fff;
      border: 1px solid #e5e5e5;
      display: flex;
      align-items: center;
      gap: 0.75rem;
      flex-direction: row-reverse;
      position: relative;
    }
    .bw-block-card .bw-card-text { flex: 1; min-width: 0; }
    .bw-block-card .bw-carousel-add-to-cart-btn {
      display: inline-block;
      width: auto;
      margin-top: 0;
      flex: 0 0 auto;
      align-self: center;
      min-width: 70px;
    }
    .bw-card-body { display: flex; align-items: center; gap: 0.75rem; flex-direction: row-reverse; position: relative; }
    .bw-card-text { flex: 1; min-width: 0; }
    .bw-product-title { font-weight: 600; font-size: 14px; }
    .bw-product-description { font-size: 13px; color: #555; }
    .bw-price-row { display: flex; gap: 0.5rem; align-items: baseline; margin-top: 0.25rem; }
    .bw-price-original { text-decoration: line-through; color: #999; font-size: 13px; }
    .bw-price { font-weight: 600; font-size: 13px; }
    .bw-price-pct {
      font-size: 12px;
      color: #fff;
      background: #ff5c00;
      padding: 0 2px;
      direction: ltr;
      position: absolute;
      top: 5px;
      left: 5px;
    }
    .bw-product-image {
      width: 64px; height: 64px; object-fit: cover; border-radius: 0.5rem; flex-shrink: 0;
    }
    .bw-product-image--placeholder { background: #f3f3f3; }
    .bw-carousel-add-to-cart-btn {
      display: block;
      margin-top: 0.5rem;
      padding: 0.3rem 1rem;
      border: 0;
      background: #111;
      color: #fff;
      cursor: pointer;
      width: 30%;
      border-radius: 0.5rem !important;
      font-size: 12px;
    }
    .bw-carousel-add-to-cart-btn:disabled { opacity: 0.6; cursor: default; }
    .bw-carousel { position: relative; }
    .bw-carousel-track {
      display: flex;
      gap: 0.75rem;
      overflow-x: auto;
      scroll-snap-type: x proximity;
      scrollbar-width: none;
      padding-inline: calc(50% - 90px);
      overscroll-behavior-x: contain;
    }
    .bw-carousel-track::-webkit-scrollbar { display: none; }
    .bw-carousel-card {
      flex: 0 0 100%;
      min-width: 100px;
      padding: 0.75rem;
      border: none;
      background: #fff;
      scroll-snap-align: center;
      transform: scale(0.85);
      opacity: 0.55;
      transition: transform 0.25s ease, opacity 0.25s ease;
    }
    .bw-carousel-card.bw-carousel-card--current {
      transform: scale(1);
      opacity: 1;
    }
    .bw-carousel-card .bw-card-body {
      flex-direction: column-reverse;
      align-items: center;
      gap: 0.5rem;
    }
    .bw-carousel-card .bw-product-image {
      width: 120px;
      height: 120px;
    }
    .bw-carousel-card .bw-card-text { text-align: center; }
    .bw-carousel-card .bw-price-row {
      justify-content: center;
    }
    .bw-carousel-card .bw-carousel-add-to-cart-btn {
      display: block;
      width: 70%;
      padding: 0.3rem 1rem;
      font-size: 14px;
      margin: 12px auto 0;
    }
    .bw-carousel-nav {
      position: absolute; top: 50%; transform: translateY(-50%);
      width: 32px; height: 32px; border-radius: 50%;
      background: #fff; border: 1px solid #ddd; cursor: pointer; z-index: 1;
      font-size: 18px;
    }
    .bw-carousel-prev { left: -8px; }
    .bw-carousel-next { right: -8px; }
  `;

  customElements.define("bw-cart-upsell", CartUpsellElement);
})();
